import { useMobileDimensions, useToken } from '@/app/hooks';
import { Mission } from '@/app/hooks/trade/tradeMission';
import { Column, Row } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import BigNumber from 'bignumber.js';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RewardButton from '../../../RewardButton';
import { DisabledRewardButton } from '../../../components/DisabledRewardButton';
import coinSymbol from './assets/coinSymbol.png';
import * as S from './styles';
import { canClaimable, getClaimStatus } from '../../../utils';
import useNavigationHandler from '../../../useNavigationHandler';

interface DepositListProps {
  mission: Mission;
  detailMission: Mission;
  isSuccessDepositMissions: boolean;
  getMissionReward: (missionId: number, tierIndex?: number, rewardAmount?: string | number) => void;
}

const DepositList = ({
  mission,
  detailMission,
  isSuccessDepositMissions,
  getMissionReward,
}: DepositListProps) => {
  const [showAll, setShowAll] = useState(false);

  const { width } = useMobileDimensions();
  const { t, i18n } = useTranslation();
  const { theme, isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { token } = useToken();
  const handleNavigation = useNavigationHandler();

  const isMobile = width <= 890;
  const missions = detailMission?.conditions?.tiers ?? [];

  // const {
  //   percent: depositPercent,
  //   slicedArray: depositSlicedArray,
  //   calculatePercent: depositCalculatePercent,
  // } = usePercent(currentDeposit, depositDataList);

  // const sortedMissions = (missions ?? []).slice().sort((a, b) => {
  //   const aClaimed = a?.status === 'claimed';
  //   const bClaimed = b?.status === 'claimed';
  //   if (aClaimed === bClaimed) return 0;
  //   return aClaimed ? -1 : 1;
  // });

  const items = showAll ? missions : missions.slice(0, 5);
  const totalRewardAmount = (missions ?? []).reduce(
    (acc: BigNumber, m: any) => acc.plus(new BigNumber(m?.rewardAmount || 0)),
    new BigNumber(0)
  );

  return (
    <S.ContainerWrapper>
      <S.Container isDarkmode={isDarkMode} isMobile={isMobile}>
        <S.RewardHeaderSection isMobile={isMobile}>
          <S.RewardInfoColumn isMobile={isMobile}>
            <S.RewardTitle isMobile={isMobile} isDarkMode={isDarkMode}>
              {t('upTo')}{' '}
              <S.RewardAmountText theme={theme} isMobile={isMobile}>
                {totalRewardAmount.toFormat()} USDT
              </S.RewardAmountText>{' '}
              {t('rewardExclamation')}
            </S.RewardTitle>

            <S.RewardDescription isMobile={isMobile}>
              {t('depositRewardDescription')}
            </S.RewardDescription>

            <S.CoinImage
              src={coinSymbol}
              alt="coin symbol"
              isMobile={isMobile}
            />
          </S.RewardInfoColumn>
        </S.RewardHeaderSection>

        <S.DesContent>
          {!isSuccessDepositMissions && (
            <S.LockOverlay isDarkmode={isDarkMode}>
              <S.LockText isDarkmode={isDarkMode}>
                {t('unlockAfterCompletingNewDepositMission')}
              </S.LockText>
            </S.LockOverlay>
          )}
          {items?.map((item, index) => {
            return(
              <S.DesContentItem key={index} isDarkmode={isDarkMode}>
                <Column style={{ gap: '10px', width: '100%' }}>
                  <Row style={{ alignItems: 'center', gap: '10px' }}>
                    <S.DesItemIcon>
                      {(() => {
                        const status = getClaimStatus(mission, index);
                        const isDone =
                          status === 'claimed' || canClaimable(mission, index);
                        return isDone ? (
                          <svg
                            width="14"
                            height="14.83"
                            viewBox="0 0 14 14.83"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.75"
                              y="0.75"
                              width="12.5"
                              height="13.33"
                              rx="2"
                              stroke={isDarkMode ? 'white' : '#424242'}
                              strokeWidth="1.5"
                              fill="none"
                            />
                            <path
                              d="M2.2 7.7L5.4 10.9L11.2 4.7"
                              stroke={isDarkMode ? 'white' : '#424242'}
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="14"
                            height="14.83"
                            viewBox="0 0 14 14.83"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.75"
                              y="0.75"
                              width="12.5"
                              height="13.33"
                              rx="2"
                              stroke={isDarkMode ? 'white' : '#424242'}
                              strokeOpacity="0.8"
                              strokeWidth="1.5"
                              fill="none"
                            />
                          </svg>
                        );
                      })()}
                    </S.DesItemIcon>
                    <S.RewardBadge>
                      {new BigNumber(item?.rewardAmount || 0).toFormat()} USDT{' '}
                      {t('rewarded')}
                    </S.RewardBadge>
                  </Row>
  
                  <S.ConditionText>
                    {t('depositAfterTransferToFutures', {
                      amount: new BigNumber(item?.minAmount || 0).toFormat(),
                    })}
                  </S.ConditionText>
  
                  {isMobile && (
                    <div style={{ marginLeft: 'auto', width: '100%' }}>
                      <RewardButton
                        missionId={mission?.id}
                        canClaimable={canClaimable(mission, index)}
                        getMissionStatus={getClaimStatus(mission, index)}
                        handleClaim={() => getMissionReward(mission.id, index, item?.rewardAmount)}
                        style={
                          isMobile ? { width: '100%', height: '32px' } : undefined
                        }
                        notCompletedComponent={
                          <DisabledRewardButton
                            variant="greenSolid"
                            style={
                              isMobile
                                ? { width: '100%', height: '32px' }
                                : undefined
                            }
                            onClick={() => handleNavigation('/buysell-crypto')}
                          >
                            {t('DepositNow')}
                          </DisabledRewardButton>
                        }
                      />
                    </div>
                  )}
                </Column>
  
                {!isMobile && (
                  <div style={{ marginLeft: 'auto' }}>
                    <RewardButton
                      missionId={mission?.id}
                      canClaimable={canClaimable(mission, index)}
                      getMissionStatus={getClaimStatus(mission, index)}
                      handleClaim={() => getMissionReward(mission.id, index, item?.rewardAmount)}
                      notCompletedComponent={
                        <DisabledRewardButton
                          variant="greenSolid"
                          onClick={() => handleNavigation('/buysell-crypto')}
                        >
                          {t('DepositNow')}
                        </DisabledRewardButton>
                      }
                    />
                  </div>
                )}
              </S.DesContentItem>
            )
          })}

          {!showAll && (missions?.length ?? 0) > 5 && (
            <S.ExternalTabButton
              isDarkmode={isDarkMode}
              onClick={() => setShowAll(true)}
            >
              <span>{t('more')}</span>
            </S.ExternalTabButton>
          )}
          {showAll && (missions?.length ?? 0) > 5 && (
            <S.ExternalTabButton
              isDarkmode={isDarkMode}
              onClick={() => setShowAll(false)}
            >
              <span>{t('hide')}</span>
            </S.ExternalTabButton>
          )}
        </S.DesContent>

        {/* <Gauges
          title={t('Current Deposit Gauge')}
          currentValue={currentDeposit}
          percent={depositPercent}
          slicedArray={depositSlicedArray}
          calculatePercent={depositCalculatePercent}
        /> */}
      </S.Container>
    </S.ContainerWrapper>
  );
};

export default DepositList;
